import React, { useState, useMemo, useEffect } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarDays, FileUp, GitMerge, Users, MapPinned, FolderOpen } from "lucide-react";
import { SavedTab } from "@/components/tabs/SavedTab";
import { Header } from "@/components/Header";
import { BackgroundSlideshow } from "@/components/BackgroundSlideshow";
import { LoginGate } from "@/components/LoginGate";
import { PlannerTab } from "@/components/tabs/PlannerTab";
import { SpecialSkTab } from "@/components/tabs/SpecialSkTab";
import { RulesTab } from "@/components/tabs/RulesTab";
import { DirectoryTab } from "@/components/tabs/DirectoryTab";
import { CellEditor } from "@/components/CellEditor";
import { MONTHS_EN, GHARS, AREA_INFO } from "@/data/rssbData";
import { AreaSetupTab } from "@/components/tabs/AreaSetupTab";
import { generateSchedule, reshuffleVariant, buildDates, overlaySpecials, setLeave as applyLeave } from "@/lib/scheduler";
import { PlannerToolbar } from "@/components/PlannerToolbar";
import { parseSchedulePdf } from "@/lib/pdfScheduleImport";
import { MONTHS_HI, defaultHM, fmt12 } from "@/data/rssbData";
import { ensureHindiSpecials } from "@/lib/specialSkParser";
import { exportPdf } from "@/lib/pdfExport";
import { exportGharSlips, exportPersonSlips } from "@/lib/slipExport";
import { printSchedule } from "@/lib/printExport";
import { exportExcel } from "@/lib/excelExport";
import * as store from "@/lib/storage";

function buildMonthOptions() {
  const opts = [];
  const start = new Date(2026, 5, 1); // June 2026
  for (let i = 0; i < 24; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    opts.push({ key: `${d.getFullYear()}-${d.getMonth()}`, year: d.getFullYear(), month: d.getMonth() });
  }
  return opts;
}

function App() {
  const monthOptions = useMemo(buildMonthOptions, []);
  const [selMonth, setSelMonth] = useState(`${2026}-${9}`); // Oct 2026 default
  const [year, month] = selMonth.split("-").map(Number);

  const [tab, setTab] = useState("planner");
  const [variantCount, setVariantCount] = useState(3);
  const [priority, setPriority] = useState("balanced");
  const [generating, setGenerating] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const [result, setResult] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [special, setSpecial] = useState([]);
  const [leave, setLeave] = useState([]);
  const [importing, setImporting] = useState(false);
  const [, setSnapVer] = useState(0);
  const [openSnap, setOpenSnap] = useState(null);
  const [editor, setEditor] = useState({ open: false, ghar: null, date: null, cell: null });

  // load persisted state when month changes (migrates old English special names to Hindi)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const raw = store.getSpecial(year, month);
      const migrated = await ensureHindiSpecials(raw);
      if (cancelled) return;
      const entries = migrated.entries;
      setSpecial(entries);
      setLeave(store.getLeave(year, month));
      const saved = store.getSchedule(year, month);
      if (saved) {
        let res = saved.result;
        if (migrated.changed) {
          store.saveSpecial(year, month, entries);
          const variants = res.variants.map((v) => overlaySpecials(v, entries));
          res = { ...res, variants };
          store.saveSchedule(year, month, { result: res, selectedId: saved.selectedId });
        }
        setResult(res);
        setSelectedId(saved.selectedId);
      } else {
        if (migrated.changed) store.saveSpecial(year, month, entries);
        setResult(null);
        setSelectedId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [selMonth]);

  const persistSpecial = (entries) => {
    setSpecial(entries);
    store.saveSpecial(year, month, entries);
    // Apply entries onto the existing schedule immediately — no need to regenerate
    if (result) {
      const variants = result.variants.map((v) => overlaySpecials(v, entries));
      persistResult({ ...result, variants }, selectedId);
      toast.success("विशेष SK एंट्री शेड्यूल में लागू हो गईं");
    }
  };

  const persistResult = (res, selId) => {
    setResult(res);
    setSelectedId(selId);
    store.saveSchedule(year, month, { result: res, selectedId: selId });
  };

  const handleGenerate = () => {
    setOpenSnap(null);
    setGenerating(true);
    setTimeout(() => {
      try {
        const res = generateSchedule(year, month, special, priority, variantCount, leave);
        const selId = res.variants[0]?.id ?? null;
        persistResult(res, selId);
        toast.success(`${res.variants.length} शेड्यूल विकल्प तैयार — सर्वश्रेष्ठ स्कोर ${res.variants[0]?.score.percent}%`);
        const idle = res.variants[0]?.idle?.length || 0;
        if (idle) toast.warning(`${idle} सेवादारों को इस माह सेवा नहीं मिल पाई (पर्याप्त सत्संग स्लॉट नहीं) — विकल्प कार्ड में देखें`);
      } catch (e) {
        toast.error("जनरेट त्रुटि: " + e.message);
      } finally {
        setGenerating(false);
      }
    }, 300);
  };

  const handleReshuffle = (variantId) => {
    applyLeave(leave);
    const fresh = reshuffleVariant(year, month, special, priority);
    const variants = result.variants.map((v) => (v.id === variantId ? fresh : v));
    const res = { ...result, variants };
    persistResult(res, selectedId === variantId ? fresh.id : selectedId);
    toast.success(`विकल्प री-शफल हुआ — स्कोर ${fresh.score.percent}%`);
  };

  const handleManual = () => {
    const dates = buildDates(year, month);
    const cells = {};
    GHARS.forEach((g) => (cells[g.id] = {}));
    const variant = {
      id: Date.now(),
      cells,
      manual: true,
      score: { percent: 0, coverage: 0, proximity: 0, rotation: 0, balance: 0 },
    };
    persistResult({ dates, variants: [variant], priority: "manual" }, variant.id);
    toast.success("खाली मैनुअल शीट तैयार — किसी सेल पर क्लिक कर भरें");
  };

  const handleCellClick = (ghar, date, cell) => setEditor({ open: true, ghar, date, cell });

  const handleCellSave = (next) => {
    const { ghar, date } = editor;
    const variants = result.variants.map((v) => {
      if (v.id !== selectedId) return v;
      const cells = { ...v.cells, [ghar.id]: { ...v.cells[ghar.id], [date.iso]: next } };
      return { ...v, cells };
    });
    persistResult({ ...result, variants }, selectedId);
    toast.success("सेल अपडेट हुई");
  };

  const handleDayTime = (iso, hm) => {
    const base = defaultHM(iso);
    const t = hm && hm !== base ? hm : null;
    const variants = result.variants.map((v) => {
      if (v.id !== selectedId) return v;
      const cells = Object.fromEntries(
        Object.entries(v.cells).map(([gid, ds]) => {
          const c = ds?.[iso];
          if (!c || c.special?.slot === "noon") return [gid, ds];
          return [gid, { ...ds, [iso]: { ...c, time: t } }];
        })
      );
      const dayTimes = { ...(v.dayTimes || {}) };
      if (t) dayTimes[iso] = t;
      else delete dayTimes[iso];
      return { ...v, cells, dayTimes };
    });
    persistResult({ ...result, variants }, selectedId);
    toast.success(t ? `${iso.split("-").reverse().join("/")} के सभी सत्संग — ${fmt12(t)}` : "सामान्य समय लागू हुआ");
  };

  const handleCellRemove = () => {
    const { ghar, date } = editor;
    const variants = result.variants.map((v) => {
      if (v.id !== selectedId) return v;
      const gharCells = { ...v.cells[ghar.id] };
      delete gharCells[date.iso];
      return { ...v, cells: { ...v.cells, [ghar.id]: gharCells } };
    });
    persistResult({ ...result, variants }, selectedId);
    toast.success("सेल हटाई गई");
  };

  const selectedVariant = result?.variants.find((v) => v.id === selectedId);

  const handleExportPdf = async (mode) => {
    if (!selectedVariant) return;
    setExportingPdf(true);
    try {
      store.commitHistory(year, month, selectedVariant);
      await exportPdf(selectedVariant, result.dates, year, month, mode);
      toast.success(mode === "bw" ? "B&W हिंदी PDF डाउनलोड हुई" : "C-हिंदी PDF डाउनलोड हुई");
    } catch (e) {
      toast.error("PDF बनाने में त्रुटि: " + e.message);
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = () => {
    if (!selectedVariant) return;
    exportExcel(selectedVariant, result.dates, year, month);
    toast.success("Excel डाउनलोड हुई");
  };

  const handleSlips = (kind) => {
    if (!selectedVariant) return;
    (kind === "ghar" ? exportGharSlips : exportPersonSlips)(selectedVariant, result.dates, year, month);
    toast.success(kind === "ghar" ? "सत्संग घर स्लिप Excel डाउनलोड हुई" : "सेवादार स्लिप Excel डाउनलोड हुई");
  };

  const handleSave = () => {
    if (!selectedVariant) return;
    const kind = result.source === "pdf" ? "PDF से" : selectedVariant.manual ? "मैनुअल" : "सिस्टम";
    const name = window.prompt("शेड्यूल का नाम", openSnap?.monthKey === selMonth ? openSnap.name : `${MONTHS_HI[month]} ${year} — ${kind}`);
    if (name === null) return;
    const savedAt = new Date().toISOString();
    persistResult({ ...result, savedAt }, selectedId);
    const id = openSnap?.monthKey === selMonth ? openSnap.id : `s${Date.now()}`;
    const snapName = name.trim() || `${MONTHS_HI[month]} ${year}`;
    store.saveSnapshot({ id, name: snapName, kind, monthKey: selMonth, savedAt, result: { ...result, savedAt }, selectedId });
    setOpenSnap({ id, name: snapName, monthKey: selMonth });
    store.commitHistory(year, month, selectedVariant);
    setSnapVer((v) => v + 1);
    toast.success("शेड्यूल सहेजा गया — 'सहेजे गए शेड्यूल' में देखें");
  };

  const openSnapshot = (s) => {
    const [y, m] = s.monthKey.split("-").map(Number);
    store.saveSchedule(y, m, { result: s.result, selectedId: s.selectedId });
    setOpenSnap({ id: s.id, name: s.name, monthKey: s.monthKey });
    if (s.monthKey === selMonth) {
      setResult(s.result);
      setSelectedId(s.selectedId);
    } else setSelMonth(s.monthKey);
    setTab("planner");
    toast.success(`"${s.name}" खुला — सेल पर क्लिक कर बदलें`);
  };

  const handleSaveLeave = (ids) => {
    setLeave(ids);
    store.saveLeave(year, month, ids);
    toast.success(`${ids.length} सेवादार छुट्टी पर — अब शेड्यूल बनाएं`);
  };

  const handleImportPdf = async (file) => {
    setImporting(true);
    try {
      const r = await parseSchedulePdf(file, special);
      const key = `${r.year}-${r.month}`;
      if (store.getSchedule(r.year, r.month) && !window.confirm(`${MONTHS_HI[r.month]} ${r.year} का मौजूदा (बिना सहेजा) शेड्यूल PDF वाले शेड्यूल से बदल जाएगा। जारी रखें?`)) return;
      setOpenSnap(null);
      const variant = { id: Date.now(), cells: r.cells, manual: true, score: { percent: 0, coverage: 0, proximity: 0, rotation: 0, balance: 0 } };
      const res = { dates: r.dates, variants: [variant], priority: "manual", source: "pdf" };
      store.saveSchedule(r.year, r.month, { result: res, selectedId: variant.id });
      if (key === selMonth) {
        setResult(res);
        setSelectedId(variant.id);
      } else setSelMonth(key);
      const filled = Object.values(r.cells).reduce((s, ds) => s + Object.keys(ds).length, 0);
      toast.success(`PDF से ${MONTHS_HI[r.month]} ${r.year} का शेड्यूल लोड हुआ (${filled} सत्संग) — अब बदलें व सहेजें`);
    } catch (e) {
      toast.error("PDF पढ़ने में त्रुटि: " + e.message);
    } finally {
      setImporting(false);
    }
  };

  const handlePrint = () => {
    if (!selectedVariant) return;
    const ok = printSchedule(selectedVariant, result.dates, year, month);
    if (!ok) toast.error("प्रिंट विंडो ब्लॉक हुई — पॉपअप की अनुमति दें");
  };

  const tabTrigger =
    "data-[state=active]:bg-gradient-to-b data-[state=active]:from-[#7a1f1f] data-[state=active]:to-[#5e1414] data-[state=active]:text-[#f3ead6] data-[state=active]:font-bold data-[state=active]:shadow-[0_3px_8px_rgba(94,20,20,0.4),inset_0_1px_0_rgba(255,255,255,0.2)] data-[state=active]:border data-[state=active]:border-[#431010] text-[#7a4a3a] hover:text-[#5e1414] hover:bg-[#f7efdc] rounded-lg transition-all gap-2 font-dev";

  return (
    <LoginGate>
    <div className="min-h-screen">
      <BackgroundSlideshow />
      <Header />
      <main className="relative z-10 max-w-[1600px] mx-auto px-3 sm:px-5 py-5">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-[#efe4ca] border border-[#e3d6b6] flex-wrap h-auto p-1.5 gap-1 rounded-xl shadow-[inset_0_2px_5px_rgba(94,20,20,0.12)]">
            <TabsTrigger value="planner" data-testid="tab-planner" className={tabTrigger}>
              <CalendarDays className="w-4 h-4" /> शेड्यूल प्लानर
            </TabsTrigger>
            <TabsTrigger value="saved" data-testid="tab-saved" className={tabTrigger}>
              <FolderOpen className="w-4 h-4" /> सहेजे गए शेड्यूल
            </TabsTrigger>
            <TabsTrigger value="special" data-testid="tab-special" className={tabTrigger}>
              <FileUp className="w-4 h-4" /> विशेष SK अपलोड
              {special.length > 0 && (
                <span className="ml-1 bg-[#f3ead6] text-[#5e1414] rounded-full px-1.5 text-[10px] font-bold shadow-[0_1px_3px_rgba(0,0,0,0.3)]">
                  {special.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules" className={tabTrigger}>
              <GitMerge className="w-4 h-4" /> नियम व समूह
            </TabsTrigger>
            <TabsTrigger value="directory" data-testid="tab-directory" className={tabTrigger}>
              <Users className="w-4 h-4" /> पाठी व SR सूची
            </TabsTrigger>
            <TabsTrigger value="area" data-testid="tab-area" className={tabTrigger}>
              <MapPinned className="w-4 h-4" /> क्षेत्र सेटअप
            </TabsTrigger>
          </TabsList>

          <TabsContent value="planner" className="mt-5">
            <PlannerToolbar
              year={year}
              month={month}
              leave={leave}
              onSaveLeave={handleSaveLeave}
              onImportPdf={handleImportPdf}
              importing={importing}
              onOpenSaved={() => setTab("saved")}
              savedCount={store.getSnapshots().length}
            />
            <PlannerTab
              monthOptions={monthOptions}
              selMonth={selMonth}
              setSelMonth={setSelMonth}
              variantCount={variantCount}
              setVariantCount={setVariantCount}
              priority={priority}
              setPriority={setPriority}
              onGenerate={handleGenerate}
              onManual={handleManual}
              generating={generating}
              result={result}
              selectedId={selectedId}
              onSelectVariant={(id) => persistResult(result, id)}
              onReshuffle={handleReshuffle}
              onCellClick={handleCellClick}
              onDayTime={handleDayTime}
              onExportPdf={handleExportPdf}
              onExportExcel={handleExportExcel}
              onSlips={handleSlips}
              onSave={handleSave}
              onPrint={handlePrint}
              exportingPdf={exportingPdf}
            />
          </TabsContent>

          <TabsContent value="saved" className="mt-5">
            <SavedTab onOpen={openSnapshot} />
          </TabsContent>

          <TabsContent value="special" className="mt-5">
            <SpecialSkTab entries={special} setEntries={persistSpecial} year={year} month={month} />
          </TabsContent>

          <TabsContent value="rules" className="mt-5">
            <RulesTab />
          </TabsContent>

          <TabsContent value="directory" className="mt-5">
            <DirectoryTab />
          </TabsContent>

          <TabsContent value="area" className="mt-5">
            <AreaSetupTab />
          </TabsContent>
        </Tabs>
      </main>

      <CellEditor
        open={editor.open}
        onClose={() => setEditor((e) => ({ ...e, open: false }))}
        ghar={editor.ghar}
        date={editor.date}
        cell={editor.cell}
        onSave={handleCellSave}
        onRemove={handleCellRemove}
        dayHM={editor.date ? selectedVariant?.dayTimes?.[editor.date.iso] : null}
        variant={selectedVariant}
      />
      <Toaster position="top-right" richColors />
      <footer className="text-center text-[#9a7a5a] text-xs py-6 font-dev">
        राधा स्वामी सत्संग ब्यास · {AREA_INFO.areaHi} क्षेत्र · {MONTHS_EN[month]} {year}
      </footer>
    </div>
    </LoginGate>
  );
}

export default App;
